// Per-project work item types cache. Loaded when a project opens and refreshed
// after any mutation. Mirrors the structure of store/cycles.ts.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { ApiWorkItemType } from "@juuplan/shared";
import {
  listWorkItemTypes,
  createWorkItemType as apiCreateWorkItemType,
  updateWorkItemType as apiUpdateWorkItemType,
  deleteWorkItemType as apiDeleteWorkItemType,
  type WorkItemTypeCreate,
  type WorkItemTypeUpdate,
} from "../lib/api";

export interface WorkItemTypesState {
  loading: boolean;
  items: ApiWorkItemType[];
  projectId: number | null;
  error: string | null;
}

const initial: WorkItemTypesState = {
  loading: true,
  items: [],
  projectId: null,
  error: null,
};

const workItemTypesSignal: Signal<WorkItemTypesState> = signal(initial);
let inFlight: Promise<void> | null = null;

async function loadWorkItemTypes(projectId: number): Promise<void> {
  try {
    const items = await listWorkItemTypes(projectId);
    workItemTypesSignal.value = { loading: false, items, projectId, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    workItemTypesSignal.value = {
      loading: false,
      items: [],
      projectId,
      error: message,
    };
  } finally {
    inFlight = null;
  }
}

/**
 * Hook: subscribe to the work item types for the given project.
 * Triggers a load when the projectId changes, and guards against returning
 * a prior project's data while the new load is still in flight.
 */
export function useWorkItemTypes(projectId: number): WorkItemTypesState {
  useEffect(() => {
    const s = workItemTypesSignal.value;
    if (!inFlight && (s.projectId !== projectId || s.error !== null)) {
      workItemTypesSignal.value = {
        loading: true,
        items: [],
        projectId,
        error: null,
      };
      inFlight = loadWorkItemTypes(projectId);
    }
  }, [projectId]);

  const state = workItemTypesSignal.value;
  if (state.projectId !== projectId) {
    return { loading: true, items: [], projectId, error: null };
  }
  return state;
}

/** Force a fresh fetch — use after any schema-changing mutation. */
export function refreshWorkItemTypes(projectId: number): Promise<void> {
  workItemTypesSignal.value = { ...workItemTypesSignal.value, loading: true };
  inFlight = loadWorkItemTypes(projectId);
  return inFlight;
}

// ---------- CRUD helpers ----------

/** Create a new work item type and refresh the cache. */
export async function addWorkItemType(
  projectId: number,
  body: WorkItemTypeCreate
): Promise<ApiWorkItemType> {
  const created = await apiCreateWorkItemType(projectId, body);
  await refreshWorkItemTypes(projectId);
  return created;
}

/** Update a work item type and refresh the cache. */
export async function patchWorkItemType(
  typeId: number,
  projectId: number,
  body: WorkItemTypeUpdate
): Promise<ApiWorkItemType> {
  const updated = await apiUpdateWorkItemType(typeId, body);
  await refreshWorkItemTypes(projectId);
  return updated;
}

/** Delete a work item type and remove it from the cache. */
export async function removeWorkItemType(
  typeId: number,
  projectId: number
): Promise<void> {
  await apiDeleteWorkItemType(typeId);
  const cur = workItemTypesSignal.value;
  if (cur.projectId === projectId) {
    workItemTypesSignal.value = {
      ...cur,
      items: cur.items.filter((t) => t.id !== typeId),
    };
  }
}
